import styles from "./Caracteristicas.module.scss";
import { Check, ChevronLeft, ChevronRight, Pencil, Plus, Trash2, X, } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { IPicture } from "shared/interfaces/IPicture";
import type { CombinationOption } from "shared/services/api/combinacoes.api";
import { api } from "shared/services/api";
import { formatVariationValue, normalizeVariationValue, sanitizeNumberInput, type NormalizedVariationValue, type VariationValueType, } from "shared/utils/variationValue";

interface VariationGroup {
  id: string;
  label: string;
  type: VariationValueType;
  unit: string | null;
  decimalPlaces: number | null;
  options: CombinationOption[];
}

interface CaracteristicasProps {
  register: any;
  errors: any;
  imagens: IPicture[];
  setImagens: React.Dispatch<React.SetStateAction<IPicture[]>>;
  caracteristicas: string[];
  setCaracteristicas: React.Dispatch<React.SetStateAction<string[]>>;
  categoryId: string;
  selectedVariationOptions: Record<string, CombinationOption[]>;
  setSelectedVariationOptions: React.Dispatch<React.SetStateAction<Record<string, CombinationOption[]>>>;
}

export const Caracteristicas = ({
  imagens,
  setImagens,
  caracteristicas,
  setCaracteristicas,
  categoryId,
  selectedVariationOptions,
  setSelectedVariationOptions,
}: CaracteristicasProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const thumbsViewportRef = useRef<HTMLDivElement | null>(null);
  const [novaCaracteristica, setNovaCaracteristica] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [imagemSelecionada, setImagemSelecionada] = useState(0);
  const [variationGroups, setVariationGroups] = useState<VariationGroup[]>([]);
  const [expandedVariations, setExpandedVariations] = useState<Record<string, boolean>>({});
  const [variationMessage, setVariationMessage] = useState("");
  const [variationToAdd, setVariationToAdd] = useState("");
  const [customInputs, setCustomInputs] = useState<Record<string, string>>({});
  const [customErrors, setCustomErrors] = useState<Record<string, string>>({});
  const [editingDraft, setEditingDraft] = useState<{
    groupId: string;
    value: string;
    text: string;
  } | null>(null);

  const addedVariationGroups = variationGroups.filter(
    (group) => selectedVariationOptions[group.id] !== undefined,
  );

  const availableVariationGroups = variationGroups.filter(
    (group) => selectedVariationOptions[group.id] === undefined,
  );

  useEffect(() => {
    const normalizedCategoryId = categoryId?.trim();

    if (!normalizedCategoryId) {
      setVariationGroups([]);
      setSelectedVariationOptions({});
      setVariationMessage("Selecione uma categoria para carregar as variações.");
      return;
    }

    async function carregarVariacoes() {
      setVariationMessage("Carregando variações...");

      try {
        const response = await api.get("/v2/variations/category", {
          params: { categoryId: normalizedCategoryId },
        });

        const definitions = Array.isArray(response.data)
          ? response.data
          : (response.data.variations ?? []);

        const groups: VariationGroup[] = await Promise.all(
          definitions.map(async (definition: any) => {
            const valuesResponse = await api.get(
              `/v2/variations/${definition.id}/values`,
            );

            const values = valuesResponse.data.values ?? valuesResponse.data;
            const unit: string | null = definition.unit ?? null;

            return {
              id: definition.id,
              label:
                definition.label ??
                definition.props?.label ??
                "Variação",
              type: definition.type === "number" ? "number" : "string",
              unit,
              decimalPlaces: definition.decimalPlaces ?? null,
              options: values.map((value: any) => ({
                variationDefinitionId: definition.id,
                variationLabel:
                  definition.label ??
                  definition.props?.label ??
                  "Variação",
                variationValueId: value.id,
                value: value.value ?? value.props?.value ?? "",
                label:
                  value.label ??
                  value.props?.label ??
                  value.value ??
                  "",
                isCustom: Boolean(value.isCustom),
                unit,
              })),
            };
          }),
        );

        const availableGroups = groups.filter(
          (group) => group.options.length > 0,
        );

        setVariationGroups(availableGroups);

        setSelectedVariationOptions((current) =>
          Object.fromEntries(
            Object.entries(current).flatMap(([variationId, options]) => {
              const group = availableGroups.find(
                (item) => item.id === variationId,
              );

              if (!group) {
                return [];
              }

              return [
                [
                  variationId,
                  options.map((option) =>
                    option.isNewCustom
                      ? { ...option, unit: group.unit }
                      : (group.options.find(
                          (item) =>
                            item.variationValueId ===
                            option.variationValueId,
                        ) ?? {
                          ...option,
                          unit: group.unit,
                        }),
                  ),
                ],
              ];
            }),
          ),
        );

        setVariationMessage(
          groups.length > 0
            ? ""
            : "Nenhuma variação disponível para esta categoria.",
        );
      } catch (error: any) {
        setVariationGroups([]);

        setVariationMessage(
          error?.response?.status === 403
            ? "Seu acesso de lojista expirou. Saia e entre novamente para carregar as variações."
            : "Não foi possível carregar as variações desta categoria.",
        );
      }
    }

    void carregarVariacoes();
  }, [categoryId, setSelectedVariationOptions]);

  function addVariation() {
    if (!variationToAdd) {
      return;
    }

    setSelectedVariationOptions((current) => ({
      ...current,
      [variationToAdd]: current[variationToAdd] ?? [],
    }));

    setExpandedVariations((current) => ({
      ...current,
      [variationToAdd]: true,
    }));

    setVariationToAdd("");
  }

  function removeVariation(variationId: string) {
    setSelectedVariationOptions((current) => {
      const { [variationId]: _removed, ...rest } = current;
      return rest;
    });
  }

  function draftsOf(groupId: string): CombinationOption[] {
    return (selectedVariationOptions[groupId] ?? []).filter(
      (option) => option.isNewCustom,
    );
  }

  function normalizeCustomLabel(
    group: VariationGroup,
    label: string,
    ignoredValue?: string,
  ): NormalizedVariationValue {
    const normalized = normalizeVariationValue(group, label);

    if (!normalized.ok) {
      return normalized;
    }

    const taken = [...group.options, ...draftsOf(group.id)].some(
      (option) =>
        option.value === normalized.value &&
        option.value !== ignoredValue,
    );

    return taken
      ? {
          ok: false,
          error: "Esse valor já existe nesta variação.",
        }
      : normalized;
  }

  function addCustomValue(group: VariationGroup) {
    const normalized = normalizeCustomLabel(
      group,
      customInputs[group.id] ?? "",
    );

    setCustomErrors((current) => ({
      ...current,
      [group.id]: normalized.ok ? "" : normalized.error,
    }));

    if (!normalized.ok) {
      return;
    }

    setSelectedVariationOptions((current) => ({
      ...current,
      [group.id]: [
        ...(current[group.id] ?? []),
        {
          variationDefinitionId: group.id,
          variationLabel: group.label,
          variationValueId: null,
          value: normalized.value,
          label: normalized.label,
          unit: group.unit,
          isNewCustom: true,
        },
      ],
    }));

    setCustomInputs((current) => ({
      ...current,
      [group.id]: "",
    }));
  }

  function removeDraft(groupId: string, value: string) {
    setSelectedVariationOptions((current) => ({
      ...current,
      [groupId]: (current[groupId] ?? []).filter(
        (option) => !(option.isNewCustom && option.value === value),
      ),
    }));
  }

  function confirmDraftEdit(group: VariationGroup) {
    if (!editingDraft) {
      return;
    }

    const normalized = normalizeCustomLabel(
      group,
      editingDraft.text,
      editingDraft.value,
    );

    setCustomErrors((current) => ({
      ...current,
      [group.id]: normalized.ok ? "" : normalized.error,
    }));

    if (!normalized.ok) {
      return;
    }

    setSelectedVariationOptions((current) => ({
      ...current,
      [group.id]: (current[group.id] ?? []).map((option) =>
        option.isNewCustom && option.value === editingDraft.value
          ? {
              ...option,
              value: normalized.value,
              label: normalized.label,
            }
          : option,
      ),
    }));

    setEditingDraft(null);
  }

  function toggleVariationValue(
    group: VariationGroup,
    option: CombinationOption,
  ) {
    setSelectedVariationOptions((current) => {
      const selected = current[group.id] ?? [];
      const optionId = option.variationValueId ?? option.value;

      const exists = selected.some(
        (item) =>
          (item.variationValueId ?? item.value) === optionId,
      );

      return {
        ...current,
        [group.id]: exists
          ? selected.filter(
              (item) =>
                (item.variationValueId ?? item.value) !== optionId,
            )
          : [...selected, option],
      };
    });
  }

  const imagemPrincipal = imagens[imagemSelecionada];

  function abrirUpload() {
    fileInputRef.current?.click();
  }

  function scrollThumbs(direction: "left" | "right") {
    thumbsViewportRef.current?.scrollBy({
      left: direction === "left" ? -220 : 220,
      behavior: "smooth",
    });
  }
    function handleAddImagem(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;

    if (!files) {
      return;
    }

    const arquivosSelecionados = Array.from(files);

    const novas: IPicture[] = arquivosSelecionados.map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));

    setImagens((prev) => [...prev, ...novas]);
    e.target.value = "";
  }
  
  function removerImagem(index: number) {
    const imagemRemovida = imagens[index];

    if (imagemRemovida?.file) {
      URL.revokeObjectURL(imagemRemovida.url);
    }

    const novasImagens = imagens.filter((_, i) => i !== index);

    setImagens(novasImagens);

    if (novasImagens.length === 0) {
      setImagemSelecionada(0);
      return;
    }

    if (index < imagemSelecionada) {
      setImagemSelecionada((current) => current - 1);
      return;
    }

    if (imagemSelecionada >= novasImagens.length) {
      setImagemSelecionada(novasImagens.length - 1);
    }
  }

  function definirImagemPrincipal(
    e: React.MouseEvent<HTMLButtonElement>,
  ) {
    e.stopPropagation();

    if (imagemSelecionada === 0) {
      return;
    }

    setImagens((prev) => {
      const imagem = prev[imagemSelecionada];

      if (!imagem) {
        return prev;
      }

      const demais = prev.filter(
        (_, index) => index !== imagemSelecionada,
      );

      return [imagem, ...demais];
    });

    setImagemSelecionada(0);
  }

  function adicionarCaracteristica() {
    if (!novaCaracteristica.trim()) {
      return;
    }

    setCaracteristicas((prev) => [
      ...prev,
      novaCaracteristica.trim(),
    ]);

    setNovaCaracteristica("");
  }

  function removerCaracteristica(index: number) {
    setCaracteristicas((prev) =>
      prev.filter((_, i) => i !== index),
    );
  }

  return (
    <div className={styles.formContainer}>
      <div className={styles.galleryContainer}>
        <div className={styles.thumbsWrapper}>
          {imagens.length > 0 && (
            <button
              type="button"
              className={`${styles.thumbArrow} ${styles.thumbArrowLeft}`}
              onClick={() => scrollThumbs("left")}
              aria-label="Ver imagens anteriores"
            >
              <ChevronLeft size={18} />
            </button>
          )}

          <div ref={thumbsViewportRef} className={styles.thumbsViewport}>
            <div className={styles.thumbsContainer}>
              <div
                className={styles.thumbAdd}
                onClick={abrirUpload}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    abrirUpload();
                  }
                }}
              >
                <Plus size={22} />
              </div>

              {imagens.map((img, index) => (
                <div
                  key={`${img.key ?? img.url}-${index}`}
                  className={`${styles.thumb} ${
                    imagemSelecionada === index
                      ? styles.thumbActive
                      : ""
                  }`}
                  onClick={() => setImagemSelecionada(index)}
                >
                  <img src={img.url} alt="" />
                </div>
              ))}
            </div>
          </div>

          {imagens.length > 0 && (
            <button
              type="button"
              className={`${styles.thumbArrow} ${styles.thumbArrowRight}`}
              onClick={() => scrollThumbs("right")}
              aria-label="Ver próximas imagens"
            >
              <ChevronRight size={18} />
            </button>
          )}
        </div>

        <div
          className={styles.previewContainer}
          onClick={() => !imagemPrincipal && abrirUpload()}
        >
          {imagemPrincipal ? (
            <>
              <img
                src={imagemPrincipal.url}
                alt=""
                className={styles.previewImage}
              />
              <button
                type="button"
                className={styles.btnRemoverImagem}
                onClick={(e) => {
                  e.stopPropagation();
                  removerImagem(imagemSelecionada);
                }}
                aria-label="Remover imagem"
              >
                <Trash2 size={14} />
              </button>

              {imagemSelecionada > 0 && (
                <button
                  type="button"
                  className={styles.btnPrincipalImagem}
                  onClick={definirImagemPrincipal}
                >
                  Principal
                </button>
              )}
            </>
          ) : (
            <div className={styles.containerImg}>
              <img
                src="/imagens/img-placeholder.png"
                alt=""
                className={styles.placeholderImage}
              />

              <p className={styles.textPlaceholder}>
                <span className={styles.linkText}>
                  Clique aqui
                </span>{" "}
                para adicionar imagem
              </p>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            accept="image/*"
            onChange={handleAddImagem}
          />
        </div>
      </div>

      <div className={styles.containerVariacoes}>
        <div className={styles.headerVariacoes}>
          <label className={styles.inputLabel}>
            Variações do produto
          </label>

          <span className={styles.variationHint}>
            Adicione uma variação e selecione os valores
            disponíveis para este produto.
          </span>
        </div>

        {variationGroups.length === 0 ? (
          <p className={styles.subTituloC}>
            {variationMessage}
          </p>
        ) : (
          <div className={styles.variationGroups}>
            {availableVariationGroups.length > 0 && (
              <div className={styles.variationPicker}>
                <select
                  className={styles.variationSelect}
                  value={variationToAdd}
                  onChange={(event) =>
                    setVariationToAdd(event.target.value)
                  }
                  aria-label="Variação a adicionar"
                >
                  <option value="">
                    Selecione uma variação
                  </option>

                  {availableVariationGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.label}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  className={styles.btnAdd}
                  onClick={addVariation}
                  disabled={!variationToAdd}
                  aria-label="Adicionar variação"
                >
                  <Plus size={18} />
                </button>
              </div>
            )}

            {addedVariationGroups.map((group) => {
              const selectedCount =
                selectedVariationOptions[group.id]?.length ?? 0;

              const expanded =
                expandedVariations[group.id] ?? false;

              return (
                <div
                  className={styles.variationGroup}
                  key={group.id}
                >
                  <div className={styles.variationHeader}>
                    <button
                      type="button"
                      className={styles.variationToggle}
                      onClick={() =>
                        setExpandedVariations((current) => ({
                          ...current,
                          [group.id]: !expanded,
                        }))
                      }
                      aria-expanded={expanded}
                    >
                      <span>{group.label}</span>
                      <span>{selectedCount} selecionado(s)</span>
                    </button>

                    <button
                      type="button"
                      className={styles.variationRemove}
                      onClick={() => removeVariation(group.id)}
                      aria-label={`Remover variação ${group.label}`}
                    >
                      <X size={14} />
                    </button>
                  </div>

                  {expanded && (
                    <div className={styles.variationOptions}>
                      {group.options.map((option) => {
                        const optionId =
                          option.variationValueId ??
                          option.value;

                        const checked = (
                          selectedVariationOptions[group.id] ?? []
                        ).some(
                          (item) =>
                            (item.variationValueId ??
                              item.value) === optionId,
                        );

                        return (
                          <label
                            className={styles.variationOption}
                            key={optionId}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() =>
                                toggleVariationValue(
                                  group,
                                  option,
                                )
                              }
                            />

                            {formatVariationValue(
                              option.label,
                              group.unit,
                            )}

                            {option.isCustom && (
                              <span className={styles.customTag}>
                                personalizado
                              </span>
                            )}
                          </label>
                        );
                      })}

                      {draftsOf(group.id).map((draft) =>
                        editingDraft?.groupId === group.id &&
                        editingDraft.value === draft.value ? (
                          <div
                            className={styles.draftOption}
                            key={draft.value}
                          >
                            <input
                              className={styles.draftInput}
                              value={editingDraft.text}
                              autoFocus
                              onChange={(event) =>
                                setEditingDraft({
                                  ...editingDraft,
                                  text:
                                    group.type === "number"
                                      ? sanitizeNumberInput(
                                          event.target.value,
                                        )
                                      : event.target.value,
                                })
                              }
                              onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                  event.preventDefault();
                                  confirmDraftEdit(group);
                                }

                                if (event.key === "Escape") {
                                  setEditingDraft(null);
                                }
                              }}
                              aria-label={`Editar ${draft.label}`}
                            />

                            <button
                              type="button"
                              onClick={() =>
                                confirmDraftEdit(group)
                              }
                              aria-label="Confirmar edição"
                            >
                              <Check size={14} />
                            </button>

                            <button
                              type="button"
                              onClick={() =>
                                setEditingDraft(null)
                              }
                              aria-label="Cancelar edição"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <div
                            className={styles.draftOption}
                            key={draft.value}
                          >
                            <span>
                              {formatVariationValue(
                                draft.label,
                                group.unit,
                              )}
                            </span>

                            <span className={styles.newTag}>
                              novo
                            </span>

                            <button
                              type="button"
                              onClick={() =>
                                setEditingDraft({
                                  groupId: group.id,
                                  value: draft.value,
                                  text: draft.label,
                                })
                              }
                              aria-label={`Editar ${draft.label}`}
                            >
                              <Pencil size={13} />
                            </button>

                            <button
                              type="button"
                              onClick={() =>
                                removeDraft(
                                  group.id,
                                  draft.value,
                                )
                              }
                              aria-label={`Remover ${draft.label}`}
                            >
                              <X size={13} />
                            </button>
                          </div>
                        ),
                      )}

                      <div className={styles.customValueEntry}>
                        <input
                          value={customInputs[group.id] ?? ""}
                          onChange={(event) =>
                            setCustomInputs((current) => ({
                              ...current,
                              [group.id]:
                                group.type === "number"
                                  ? sanitizeNumberInput(
                                      event.target.value,
                                    )
                                  : event.target.value,
                            }))
                          }
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              addCustomValue(group);
                            }
                          }}
                          inputMode={
                            group.type === "number"
                              ? "decimal"
                              : undefined
                          }
                          placeholder={
                            group.type === "number"
                              ? "Outro valor (ex.: 10,5)"
                              : "Outro valor"
                          }
                          aria-label={`Outro valor para ${group.label}`}
                        />

                        {group.unit && (
                          <span className={styles.unitSuffix}>
                            {group.unit}
                          </span>
                        )}

                        <button
                          type="button"
                          className={styles.btnAdd}
                          onClick={() => addCustomValue(group)}
                          aria-label={`Adicionar valor a ${group.label}`}
                        >
                          <Plus size={16} />
                        </button>
                      </div>

                      {customErrors[group.id] && (
                        <p className={styles.customError}>
                          {customErrors[group.id]}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className={styles.containerCaracteristicas}>
        <div className={styles.headerCaracteristicas}>
          <label className={styles.inputLabel}>
            Características
          </label>

          <button
            type="button"
            className={styles.btnAdd}
            onClick={() => setOpenModal(true)}
          >
            <Plus size={18} />
          </button>
        </div>

        <div className={styles.listaCaracteristicas}>
          {caracteristicas.map((item, index) => (
            <div
              key={`${item}-${index}`}
              className={styles.caracteristicaItem}
            >
              <span>{item}</span>

              <button
                type="button"
                onClick={() => removerCaracteristica(index)}
                aria-label={`Remover ${item}`}
              >
                <X size={13} />
              </button>
            </div>
          ))}
        </div>

        {caracteristicas.length === 0 && (
          <p className={styles.subTituloC}>
            Forneça ao menos 1 característica do produto
          </p>
        )}
      </div>

      {openModal && (
        <div className={styles.overlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <div>
                <h3>Nova característica</h3>
                <p>
                  Informe uma característica do produto.
                </p>
              </div>

              <button
                type="button"
                className={styles.modalClose}
                onClick={() => {
                  setOpenModal(false);
                  setNovaCaracteristica("");
                }}
                aria-label="Fechar"
              >
                <X size={16} />
              </button>
            </div>

            <input
              value={novaCaracteristica}
              onChange={(e) =>
                setNovaCaracteristica(e.target.value)
              }
              placeholder="Digite a característica"
              className={styles.modalInput}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  adicionarCaracteristica();
                  setOpenModal(false);
                }

                if (e.key === "Escape") {
                  e.preventDefault();
                  setOpenModal(false);
                  setNovaCaracteristica("");
                }
              }}
            />

            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.btnCancelar}
                onClick={() => {
                  setOpenModal(false);
                  setNovaCaracteristica("");
                }}
              >
                Cancelar
              </button>

              <button
                type="button"
                className={styles.btnAdicionar}
                onClick={() => {
                  adicionarCaracteristica();
                  setOpenModal(false);
                }}
                disabled={!novaCaracteristica.trim()}
              >
                Adicionar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};